import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import type { EditorPattern } from "@/components/document-editor/fields-context";
import { FormField } from "@/components/ui/form-field";
import { OverlayShell } from "@/components/ui/overlay";
import { usePatternCreate, usePatternUpdate } from "@/hooks/use-terms";
import { errorCode } from "@/lib/error-code";
import { kebab } from "@/lib/slug";
import { cn } from "@/lib/utils";

export function PatternDialog({
	termId,
	draft,
	patterns,
	onClose,
	onSaved,
}: {
	termId: number;
	draft: { mode: "create" } | { mode: "edit"; pattern: EditorPattern };
	patterns: EditorPattern[];
	onClose(): void;
	onSaved(pattern: EditorPattern): void;
}) {
	const [label, setLabel] = useState(draft.mode === "create" ? "" : draft.pattern.label);
	const [name, setName] = useState(draft.mode === "create" ? "" : draft.pattern.name);
	const [nameEdited, setNameEdited] = useState(false);
	const [minimumItems, setMinimumItems] = useState<0 | 1>(
		draft.mode === "create" ? 1 : draft.pattern.minimumItems,
	);
	const create = usePatternCreate();
	const update = usePatternUpdate();
	const pending = create.isPending || update.isPending;
	const title = draft.mode === "create" ? "Criar padrão" : "Editar padrão";

	const changeLabel = (value: string) => {
		setLabel(value);

		if (draft.mode === "create" && !nameEdited) {
			setName(kebab(value));
		}
	};

	const handleSubmit = () => {
		const trimmed = label.trim();

		if (trimmed === "" || name === "" || pending) {
			return;
		}

		if (draft.mode === "edit") {
			update.mutate(
				{
					termId,
					patternId: draft.pattern.id,
					label: trimmed,
					minimumItems,
				},
				{
					onSuccess: () => {
						onSaved({ ...draft.pattern, label: trimmed, minimumItems });
						onClose();
					},
					onError: () => toast.error("Padrão não atualizado"),
				},
			);

			return;
		}

		create.mutate(
			{ termId, name, label: trimmed, minimumItems },
			{
				onSuccess: (pattern) => {
					onSaved({
						id: pattern.id,
						name: pattern.name,
						label: pattern.label,
						minimumItems: pattern.minimum_items,
					});
					onClose();
				},
				onError: (error) => {
					const code = errorCode(error);
					toast.error(
						code === "DUPLICATE_PATTERN_NAME"
							? "Já existe um padrão com esse nome"
							: "Padrão não criado",
					);
				},
			},
		);
	};

	return (
		<OverlayShell label={title} onClose={onClose} className="items-center justify-center p-4">
			<div className="overlay-panel max-w-lg gap-4 p-5">
				<h2 className="type-section-title text-foreground">{title}</h2>

				{draft.mode === "create" && patterns.length > 0 && (
					<div className="flex flex-col gap-2">
						<p className="text-xs font-medium text-foreground">Usar padrão existente</p>
						<div className="flex flex-wrap gap-2">
							{patterns.map((pattern) => (
								<button
									key={pattern.id}
									type="button"
									onClick={() => {
										onSaved(pattern);
										onClose();
									}}
									className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
								>
									{pattern.label}
								</button>
							))}
						</div>
					</div>
				)}

				<FormField
					label="Nome"
					value={label}
					onChange={changeLabel}
					placeholder="Linhas de pagamento"
					autoFocus
				/>

				{draft.mode === "create" && (
					<FormField
						label="Identificador"
						value={name}
						onChange={(value) => {
							setName(kebab(value));
							setNameEdited(true);
						}}
					/>
				)}

				<fieldset className="flex flex-col gap-2">
					<legend className="text-xs font-medium text-foreground">Quantidade mínima</legend>
					<div className="grid grid-cols-2 gap-2">
						{(
							[
								{ value: 1, label: "Ao menos um" },
								{ value: 0, label: "Pode ficar vazio" },
							] as const
						).map((option) => (
							<button
								key={option.value}
								type="button"
								aria-pressed={minimumItems === option.value}
								onClick={() => setMinimumItems(option.value)}
								className={cn(
									"rounded-md border px-3 py-2 text-left text-xs font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
									minimumItems === option.value && "border-primary bg-primary/10 text-primary",
									minimumItems !== option.value && "border-border text-muted-foreground",
								)}
							>
								{option.label}
							</button>
						))}
					</div>
				</fieldset>

				<div className="flex justify-end gap-2">
					<Button type="button" variant="secondary" size="sm" onClick={onClose}>
						Cancelar
					</Button>
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={label.trim() === "" || name === "" || pending}
						onClick={handleSubmit}
					>
						{pending && <Loader2 className="size-4 animate-spin" />}
						{title}
					</Button>
				</div>
			</div>
		</OverlayShell>
	);
}
